import type { MessageRule, MessageTemplate, ParameterSet, Dataset, DatasetRow } from '@k-ucap/shared';
import { DatasetRowStrategy } from '@k-ucap/shared';
import type { TemplateResolverService, ResolveOptions } from './TemplateResolverService.js';
import type { ParameterSetService } from './ParameterSetService.js';
import { createDatasetRowSelector } from './TemplateResolverService.js';
import type {
  DatasetRepository,
  MessageTemplateRepository,
  ParameterSetRepository,
} from '@/infrastructure/repositories/RepositoryInterfaces.js';

export interface ScenarioAssetResolverService {
  resolveMessageRules(rules: MessageRule[]): Promise<MessageRule[]>;
}

export interface ScenarioAssetResolverConfig {
  messageTemplateRepository: MessageTemplateRepository;
  parameterSetRepository: ParameterSetRepository;
  datasetRepository: DatasetRepository;
  templateResolver: TemplateResolverService;
  parameterSetService: ParameterSetService;
}

export const createScenarioAssetResolverService = (
  config: ScenarioAssetResolverConfig
): ScenarioAssetResolverService => {
  const cache: {
    templates: Record<string, MessageTemplate | null>;
    baseTemplates: Record<string, MessageTemplate | null>;
    parameterSets: Record<string, ParameterSet | null>;
    datasets: Record<string, Dataset | null>;
  } = {
    templates: {},
    baseTemplates: {},
    parameterSets: {},
    datasets: {},
  };

  const getTemplate = async (id: string): Promise<MessageTemplate | null> => {
    if (!(id in cache.templates)) {
      cache.templates[id] = (await config.messageTemplateRepository.findById(id)) ?? null;
    }
    return cache.templates[id];
  };

  const getBaseTemplate = async (template: MessageTemplate): Promise<MessageTemplate | null> => {
    if (!template.baseTemplateId) return null;
    if (!(template.baseTemplateId in cache.baseTemplates)) {
      cache.baseTemplates[template.baseTemplateId] =
        (await config.messageTemplateRepository.findById(template.baseTemplateId)) ?? null;
    }
    return cache.baseTemplates[template.baseTemplateId];
  };

  const getParameterSet = async (id: string): Promise<ParameterSet | null> => {
    if (!(id in cache.parameterSets)) {
      cache.parameterSets[id] = (await config.parameterSetRepository.findById(id)) ?? null;
    }
    return cache.parameterSets[id];
  };

  const getDataset = async (id: string): Promise<Dataset | null> => {
    if (!(id in cache.datasets)) {
      cache.datasets[id] = (await config.datasetRepository.findById(id)) ?? null;
    }
    return cache.datasets[id];
  };

  const resolveMessageRules = async (rules: MessageRule[]): Promise<MessageRule[]> => {
    const rowState: Record<string, number> = {};
    const selector = createDatasetRowSelector();

    return Promise.all(
      rules.map(async (rule) => {
        // If no asset reference, keep existing behavior
        if (!rule.messageTemplateId && !rule.parameterSetId && !rule.parameterSetIds && !rule.datasetId) {
          return rule;
        }

        const template = rule.messageTemplateId ? await getTemplate(rule.messageTemplateId) : null;
        const base = template ? await getBaseTemplate(template) : null;

        const parameterSetIds = rule.parameterSetIds ?? (rule.parameterSetId ? [rule.parameterSetId] : []);
        const parameterSets: ParameterSet[] = [];
        for (const id of parameterSetIds) {
          const ps = await getParameterSet(id);
          if (ps) parameterSets.push(ps);
        }
        const filteredParameterSets = template
          ? config.parameterSetService.filterForMessageType(parameterSets, template.messageType)
          : parameterSets;

        const dataset = rule.datasetId ? await getDataset(rule.datasetId) : null;

        let resolvedPayload: Record<string, unknown> = { ...rule.payloadTemplate };
        let datasetRow: DatasetRow | undefined;

        if (template) {
          if (dataset) {
            rowState[dataset.id] = (rowState[dataset.id] ?? 0) + 1;
            const strategy = rule.datasetRowStrategy ?? DatasetRowStrategy.SEQUENTIAL;
            datasetRow = selector.selectRow(dataset, strategy, rowState[dataset.id] - 1);
          }
          const resolveOptions: ResolveOptions = {
            parameterSets: filteredParameterSets,
            dataset: dataset ?? undefined,
            datasetRow,
            runtimeOverrides: { ...rule.payloadTemplate, ...rule.runtimeOverrides },
            context: {
              sequence: 1,
              timestamp: new Date().toISOString(),
            },
          };
          const resolved = config.templateResolver.resolve(template, base ?? undefined, resolveOptions);
          resolvedPayload = { ...resolved.resolvedPayload };
        }

        return {
          ...rule,
          payloadTemplate: resolvedPayload,
        };
      })
    );
  };

  return { resolveMessageRules };
};
